import { createSelector } from '@reduxjs/toolkit';
import { contactsNotDeleted, employeesNotDeleted, partnersNotDeleted } from 'src/store/selectors';
import type { EmergencyContact } from 'src/outskirts/account/emergency/emergency-view-model';
import type { ContactPersonViewModel, PartnerViewModel } from '@trinnvis/dabih-api-client';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import type { EmployeeViewModelWithName } from 'src/store/types';
import { sortBy } from 'lodash';

export const getEmergencyPartnersAndContacts = createSelector(
  partnersNotDeleted,
  contactsNotDeleted,
  function (partners: PartnerViewModel[], contacts: ContactPersonViewModel[]): EmergencyContact[] {
    const result: EmergencyContact[] = [];
    const emergencyPartners = partners.filter((p) => {
      return p.pages?.includes(pageIds.emergency);
    });
    emergencyPartners.forEach((partner) => {
      if (partner.name && (partner.email || partner.phone)) {
        result.push({
          uuid: partner.uuid,
          name: partner.name,
          organizationName: '',
          phone: partner.phone ?? '',
          email: partner.email ?? '',
        });
      }
      if (partner.name) {
        const emergencyContacts = contacts.filter((c) => c.partnerUuid === partner.uuid);
        emergencyContacts.forEach((contact) => {
          if ((contact.firstName || contact.lastName) && (contact.email || contact.mobilePhone)) {
            let name: string;
            if (contact.firstName && contact.lastName) {
              name = contact.firstName + ' ' + contact.lastName;
            } else {
              name = contact.firstName ?? contact.lastName;
            }
            result.push({
              uuid: contact.uuid,
              name,
              organizationName: partner.name,
              phone: contact.mobilePhone ?? '',
              email: contact.email ?? '',
            });
          }
        });
      }
    });
    return sortBy(result, (r) => r.name);
  },
);

export const getEmployeesWithContactInfo = createSelector(
  employeesNotDeleted,
  function (items: EmployeeViewModelWithName[]): EmergencyContact[] {
    const result: EmergencyContact[] = [];
    items.forEach((item) => {
      if (item.name && (item.email || item.phone)) {
        result.push({
          uuid: item.uuid,
          name: item.name,
          phone: item.phone ?? '',
          email: item.email ?? '',
        });
      }
    });
    return sortBy(result, (r) => r.name);
  },
);

export const getPartnersWithContactInfo = createSelector(
  partnersNotDeleted,
  function (items: PartnerViewModel[]): EmergencyContact[] {
    const result: EmergencyContact[] = [];
    items.forEach((item) => {
      if (item.name && (item.email || item.phone)) {
        result.push({
          uuid: item.uuid,
          name: item.name,
          phone: item.phone ?? '',
          email: item.email ?? '',
        });
      }
    });
    return sortBy(result, (r) => r.name);
  },
);

export const getContactsWithContactInfo = createSelector(
  contactsNotDeleted,
  partnersNotDeleted,
  function (contacts: ContactPersonViewModel[], partners: PartnerViewModel[]): EmergencyContact[] {
    const result: EmergencyContact[] = [];
    contacts.forEach((contact) => {
      if ((contact.firstName || contact.lastName) && (contact.email || contact.mobilePhone)) {
        let name: string;
        if (contact.firstName && contact.lastName) {
          name = contact.firstName + ' ' + contact.lastName;
        } else {
          name = contact.firstName ?? contact.lastName;
        }
        let organizationName = '';
        const partner = partners.find((p) => p.uuid === contact.partnerUuid);
        if (partner && partner.name) {
          organizationName = partner.name;
        }
        result.push({
          uuid: contact.uuid,
          name,
          organizationName,
          phone: contact.mobilePhone ?? '',
          email: contact.email ?? '',
        });
      }
    });
    return sortBy(result, (r) => r.name);
  },
);

export const getPartnersAndContactsWithContactInfo = createSelector(
  getPartnersWithContactInfo,
  getContactsWithContactInfo,
  function (partners: EmergencyContact[], contacts: EmergencyContact[]): EmergencyContact[] {
    return sortBy([...partners, ...contacts], (i) => i.name);
  },
);

export const getPartnersAndPeopleWithContactInfo = createSelector(
  getPartnersWithContactInfo,
  getContactsWithContactInfo,
  getEmployeesWithContactInfo,
  function (
    partners: EmergencyContact[],
    contacts: EmergencyContact[],
    employees: EmergencyContact[],
  ): EmergencyContact[] {
    return sortBy([...partners, ...contacts, ...employees], (i) => i.name);
  },
);
